import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const { user, logout, subscription: storeSubscription } = useAuthStore();
  const location = useLocation();
  const isRTL = i18n.language === 'ar';

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [subscription, setSubscription] = useState<any>(null);
  const [readiness, setReadiness] = useState<number | null>(null);

  const profileRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  const isPremium = subscription && (subscription.plan?.name === 'Pro' || subscription.plan?.name === 'Enterprise' || subscription.plan?.has_full_access === 1);

  const formatPlanLabel = (sub: typeof subscription) => {
    if (!sub) return t('nav.freePlan');
    const planName = sub.plan?.name || 'Basic';
    if (planName === 'Basic') return t('nav.freePlan');
    if (sub.isExpired) return `${planName} • ${t('dashboard.expired')}`;
    if (sub.daysRemaining !== null && sub.daysRemaining >= 0) {
      return `${planName} • ${t('dashboard.daysLeft', { days: sub.daysRemaining })}`;
    }
    return planName;
  };

  const formatPlanExpiry = (sub: typeof subscription) => {
    if (!sub || sub.plan?.name === 'Basic') return t('dashboard.noExpiry');
    if (sub.isExpired) return t('dashboard.expiredOn', { date: new Date(sub.end_date).toLocaleDateString() });
    return t('dashboard.expiresOn', { date: new Date(sub.end_date).toLocaleDateString() });
  };

  /* ── Scroll detection for shadow ── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── Readiness from localStorage ── */
  useEffect(() => {
    if (storeSubscription) setSubscription(storeSubscription);
    const stored = localStorage.getItem('pmp_readiness_score');
    if (stored) setReadiness(parseInt(stored, 10));
  }, [user?.id, storeSubscription]);

  /* ── Close dropdowns on route change ── */
  useEffect(() => {
    setMobileOpen(false);
    setProfileOpen(false);
    setNotifOpen(false);
  }, [location.pathname]);

  /* ── Click outside to close dropdowns ── */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── Lock body scroll when mobile drawer open ── */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const toggleLang = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('lang', newLang);
  };

  const isActive = (path: string) => {
    if (path === '/' ) return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const readinessColor = readiness === null
    ? 'bg-muted text-muted-foreground'
    : readiness >= 76 ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
    : readiness >= 51 ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
    : 'bg-red-500/15 text-red-600 dark:text-red-400';

  const navLinks = [
    { to: '/dashboard', label: t('nav.dashboard'), icon: '📊' },
    { to: '/exam', label: t('nav.practice'), icon: '📝', match: '/exam' },
    { to: '/exam', label: t('nav.examMode'), icon: '⏱️', match: '/exam', mode: 'exam' },
    { to: '/dashboard', label: t('nav.analytics'), icon: '📈', match: '/dashboard', hash: '#analytics' },
    { to: '/pricing', label: t('nav.pricing'), icon: '💎' },
    { to: '/blog', label: t('nav.blog'), icon: '📚' },
  ];

  const mobileLinks = [
    { to: '/dashboard', label: t('nav.dashboard'), icon: '📊' },
    { to: '/exam', label: t('nav.practice'), icon: '📝' },
    { to: '/exam', label: t('nav.examMode'), icon: '⏱️' },
    { to: '/dashboard', label: t('nav.analytics'), icon: '📈' },
    { to: '/pricing', label: t('nav.pricing'), icon: '💎' },
    { to: '/blog', label: t('nav.blog'), icon: '📚' },
    { to: '/contact', label: t('nav.contact'), icon: '✉️' },
  ];

  const profileLinks = [
    { to: '/dashboard', label: t('nav.dashboard'), icon: '📊' },
    { to: '/profile', label: t('nav.profile'), icon: '👤' },
    { to: '/pricing', label: t('nav.subscription'), icon: '💳' },
    { to: '/contact', label: t('nav.contactSupport'), icon: '🛟', badge: !isPremium ? t('nav.supportBadge') : null },
    { to: '/profile', label: t('nav.settings'), icon: '⚙️' },
  ];

  const displayName = user?.display_name || user?.email?.split('@')[0] || 'User';
  const avatarChar = (user?.display_name || user?.email || 'U').charAt(0).toUpperCase();

  return (
    <>
      {/* ── Sticky Header ── */}
      <header
        className={`sticky top-0 z-50 w-full transition-all duration-200 ${
          scrolled
            ? 'bg-card/80 backdrop-blur-xl border-b border-border shadow-sm'
            : 'bg-card/60 backdrop-blur-md border-b border-border/50'
        }`}
        style={{ height: '72px' }}
      >
        <div className="mx-auto h-full px-4 lg:px-6 flex items-center justify-between gap-4" style={{ maxWidth: '1400px' }}>
          {/* ── LEFT: Logo + Badge ── */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link to="/" className="flex items-center gap-2.5 group">
              {/* Logo Icon */}
              <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-200">
                <span className="text-white font-bold text-sm">P</span>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary to-purple-600 opacity-0 group-hover:opacity-100 blur-md transition-opacity duration-200" />
              </div>
              {/* Logo Text */}
              <div className="hidden sm:flex flex-col leading-tight">
                <span className="font-bold text-sm">PMP Exam Simulator</span>
                <span className="text-[10px] text-muted-foreground font-medium">{t('nav.ecoBadge')}</span>
              </div>
            </Link>
          </div>

          {/* ── CENTER: Nav Links (desktop) ── */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link, i) => {
              const active = isActive(link.to);
              return (
                <Link
                  key={i}
                  to={link.to}
                  className={`relative px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-[1.02] ${
                    active
                      ? 'text-primary bg-primary/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  }`}
                >
                  {link.label}
                  {active && (
                    <span className="absolute -bottom-px left-3 right-3 h-0.5 rounded-full bg-primary" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* ── RIGHT: Actions ── */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* PMP Readiness Indicator */}
            {user && readiness !== null && (
              <Link
                to="/dashboard"
                className={`hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold ${readinessColor} transition-all hover:scale-105`}
                title="View dashboard analytics"
              >
                <span className={`w-2 h-2 rounded-full ${readinessColor.includes('emerald') ? 'bg-emerald-500' : readinessColor.includes('amber') ? 'bg-amber-500' : 'bg-red-500'} animate-pulse`} />
                {readiness}% {t('nav.ready')}
              </Link>
            )}

            {/* Language Toggle */}
            <button
              onClick={toggleLang}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-border bg-background/50 hover:bg-accent transition-all hover:scale-105"
              title="Switch language"
            >
              <span className="text-sm">{isRTL ? '🇬🇧' : '🇸🇦'}</span>
              <span className="hidden sm:inline">{isRTL ? 'EN' : 'ع'}</span>
            </button>

            {/* Notifications */}
            {user && (
              <div ref={notifRef} className="relative hidden sm:block">
                <button
                  onClick={() => setNotifOpen(!notifOpen)}
                  className="relative w-9 h-9 rounded-lg flex items-center justify-center border border-border bg-background/50 hover:bg-accent transition-all hover:scale-105"
                >
                  <span className="text-base">🔔</span>
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">2</span>
                </button>
                {notifOpen && (
                  <div className="absolute right-0 mt-2 w-72 rounded-xl border border-border bg-popover shadow-premium-lg overflow-hidden animate-fade-in">
                    <div className="p-3 border-b border-border">
                      <p className="text-sm font-semibold">{t('nav.notifications')}</p>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {[
                        { icon: '🎯', title: 'New practice questions', desc: '5 new Hyper-Expert questions added', time: '2h ago' },
                        { icon: '📈', title: 'Readiness improved', desc: 'Your score went up by 5%', time: '1d ago' },
                      ].map((n, i) => (
                        <div key={i} className="p-3 border-b border-border last:border-0 hover:bg-accent transition-colors cursor-pointer">
                          <div className="flex items-start gap-2">
                            <span className="text-lg flex-shrink-0">{n.icon}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold truncate">{n.title}</p>
                              <p className="text-[11px] text-muted-foreground truncate">{n.desc}</p>
                              <p className="text-[10px] text-muted-foreground mt-0.5">{n.time}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Upgrade to Pro / Pro Badge */}
            {user && !isPremium && (
              <Link
                to="/pricing"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 transition-all hover:scale-105 shadow-md"
              >
                ⚡ {t('nav.upgrade')}
              </Link>
            )}
            {user && isPremium && (
              <span className="hidden sm:flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-md">
                ⭐ {t('nav.proBadge')}
              </span>
            )}

            {/* User Profile Dropdown */}
            {user ? (
              <div ref={profileRef} className="relative">
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-2 px-1.5 py-1.5 rounded-lg border border-border bg-background/50 hover:bg-accent transition-all hover:scale-105"
                >
                  {/* Avatar */}
                  {user.photo_url ? (
                    <img src={user.photo_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-bold text-xs">
                      {avatarChar}
                    </div>
                  )}
                  {/* Name + Plan */}
                  <div className="hidden md:flex flex-col items-start leading-tight">
                    <span className="text-xs font-semibold max-w-[100px] truncate">{displayName}</span>
                    <span className={`text-[9px] font-medium ${isPremium ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
                      {formatPlanLabel(subscription)}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground hidden md:inline">▾</span>
                </button>

                {profileOpen && (
                  <div className={`absolute mt-2 w-60 rounded-xl border border-border bg-popover shadow-premium-lg overflow-hidden animate-fade-in ${isRTL ? 'left-0' : 'right-0'}`}>
                    {/* User info header */}
                    <div className="p-3 border-b border-border bg-gradient-to-br from-primary/5 to-purple-500/5">
                      <div className="flex items-center gap-2.5">
                        {user.photo_url ? (
                          <img src={user.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                            {avatarChar}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate">{displayName}</p>
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          <span className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[9px] font-bold ${isPremium ? 'bg-amber-500/15 text-amber-600' : 'bg-muted text-muted-foreground'}`}>
                            {isPremium ? `⭐ ${subscription?.plan?.name || t('nav.proMember')}` : subscription?.plan?.name || t('nav.freePlan')}
                          </span>
                          <p className="text-[9px] text-muted-foreground mt-0.5 truncate">{formatPlanExpiry(subscription)}</p>
                        </div>
                      </div>
                    </div>
                    {/* Links */}
                    <div className="p-1.5">
                      {profileLinks.map((pl, i) => (
                        <Link
                          key={i}
                          to={pl.to}
                          className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm hover:bg-accent transition-colors"
                        >
                          <span className="text-base">{pl.icon}</span>
                          <span className="text-muted-foreground">{pl.label}</span>
                          {pl.badge && (
                            <span className="ml-auto px-1.5 py-0.5 rounded text-[9px] font-bold bg-primary/15 text-primary">
                              {pl.badge}
                            </span>
                          )}
                        </Link>
                      ))}
                      {/* Divider */}
                      <div className="my-1.5 h-px bg-border" />
                      {/* Logout */}
                      <button
                        onClick={() => { logout(); setProfileOpen(false); }}
                        className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm hover:bg-red-500/10 text-red-600 transition-colors"
                      >
                        <span className="text-base">🚪</span>
                        <span>{t('nav.logout')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Login button for non-authenticated users */
              <Link
                to="/login"
                className="px-4 py-1.5 rounded-lg text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-all hover:scale-105 shadow-sm"
              >
                {t('nav.login')}
              </Link>
            )}

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center border border-border bg-background/50 hover:bg-accent transition-all"
              aria-label={t('nav.menu')}
            >
              <div className="flex flex-col gap-1 w-4">
                <span className={`h-0.5 bg-foreground rounded-full transition-all duration-200 ${mobileOpen ? 'rotate-45 translate-y-1.5' : ''}`} />
                <span className={`h-0.5 bg-foreground rounded-full transition-all duration-200 ${mobileOpen ? 'opacity-0' : ''}`} />
                <span className={`h-0.5 bg-foreground rounded-full transition-all duration-200 ${mobileOpen ? '-rotate-45 -translate-y-1.5' : ''}`} />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile Drawer ── */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden animate-fade-in"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer */}
          <div
            ref={mobileRef}
            className={`fixed top-0 z-50 w-80 max-w-[85vw] h-full bg-card border-${isRTL ? 'l' : 'r'} border-border shadow-premium-lg lg:hidden overflow-y-auto animate-fade-in`}
            style={isRTL ? { right: 0 } : { left: 0 }}
          >
            {/* Drawer Header */}
            <div className="sticky top-0 z-10 bg-card/95 backdrop-blur-md border-b border-border p-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-bold text-xs">P</div>
                <span className="font-bold text-sm">PMP Exam Simulator</span>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-accent transition-colors"
              >
                <span className="text-lg">✕</span>
              </button>
            </div>

            {/* User info (if logged in) */}
            {user && (
              <div className="p-4 border-b border-border bg-gradient-to-br from-primary/5 to-purple-500/5">
                <div className="flex items-center gap-3">
                  {user.photo_url ? (
                    <img src={user.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-bold">
                      {avatarChar}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{displayName}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    <span className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[9px] font-bold ${isPremium ? 'bg-amber-500/15 text-amber-600' : 'bg-muted text-muted-foreground'}`}>
                      {isPremium ? `⭐ ${subscription?.plan?.name || t('nav.proMember')}` : subscription?.plan?.name || t('nav.freePlan')}
                    </span>
                    <p className="text-[9px] text-muted-foreground mt-0.5 truncate">{formatPlanExpiry(subscription)}</p>
                  </div>
                </div>
                {/* Readiness badge in drawer */}
                {readiness !== null && (
                  <Link
                    to="/dashboard"
                    className={`mt-3 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold ${readinessColor}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${readinessColor.includes('emerald') ? 'bg-emerald-500' : readinessColor.includes('amber') ? 'bg-amber-500' : 'bg-red-500'} animate-pulse`} />
                    {readiness}% {t('nav.ready')}
                  </Link>
                )}
              </div>
            )}

            {/* Nav Links */}
            <nav className="p-3 space-y-1">
              {mobileLinks.map((link, i) => {
                const active = isActive(link.to);
                return (
                  <Link
                    key={i}
                    to={link.to}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      active
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    }`}
                  >
                    <span className="text-lg">{link.icon}</span>
                    {link.label}
                    {active && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary" />}
                  </Link>
                );
              })}
            </nav>

            {/* Divider */}
            <div className="mx-3 h-px bg-border" />

            {/* Profile links */}
            {user && (
              <nav className="p-3 space-y-1">
                {profileLinks.map((pl, i) => (
                  <Link
                    key={i}
                    to={pl.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-all"
                  >
                    <span className="text-lg">{pl.icon}</span>
                    {pl.label}
                  </Link>
                ))}
                <button
                  onClick={() => { logout(); setMobileOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-500/10 transition-all"
                >
                  <span className="text-lg">🚪</span>
                  {t('nav.logout')}
                </button>
              </nav>
            )}

            {/* Upgrade CTA */}
            {user && !isPremium && (
              <div className="p-3">
                <Link
                  to="/pricing"
                  className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 transition-all shadow-md"
                >
                  ⚡ {t('nav.upgrade')}
                </Link>
              </div>
            )}

            {/* Language toggle in drawer */}
            <div className="p-3 border-t border-border">
              <button
                onClick={toggleLang}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium border border-border hover:bg-accent transition-all"
              >
                <span className="text-base">{isRTL ? '🇬🇧' : '🇸🇦'}</span>
                {isRTL ? 'English' : 'العربية'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
